import { Alert, Box, Button, CircularProgress, Container, FormControl, InputLabel, MenuItem, Select, styled, TextField, Typography } from '@mui/material';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import palette from '../../../ui/palette';
import { useUpload, DocType } from '.';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export default function DocUploadSelect() {
  const { t } = useTranslation();
  const {
    addFiles,
    docType,
    setDocType,
    frequency,
    setFrequency,
    employerEmail,
    setEmployerEmail,
    documentRequiredCount,
    setDocumentRequiredCount,
    isValidating,
    validationError,
    setValidationError,
    showValidationError,
    setShowValidationError,
    validationWarnings,
    fallbackTriggered,
    lockedDocType,
  } = useUpload();

  const lockedLabelKey =
    lockedDocType === 'Paystub'
      ? 'verification.type.document.fallback.lockedSelectLabelPaystub'
      : 'verification.type.document.fallback.lockedSelectLabelBankStatement';
  const subFlowBannerKey =
    lockedDocType === 'Paystub'
      ? 'verification.type.document.fallback.uploadSubFlowBannerPaystub'
      : 'verification.type.document.fallback.uploadSubFlowBannerBankStatement';

  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <Container maxWidth="xs" disableGutters sx={{ py: { xs: 2, md: 3 } }}>
      <Typography variant="h2" textAlign="center" sx={{ fontWeight: 500, mb: 3 }}>
        {t('verification.type.document.documentTypeLabel')}
      </Typography>

      {lockedDocType ? (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t(subFlowBannerKey)}
          </Alert>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t(lockedLabelKey)}
          </Typography>
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 1,
            mb: 2,
          }}
        >
          <Typography variant="h6">{t('verification.type.document.selectDocumentType')}</Typography>
          <FormControl fullWidth sx={{ flexBasis: { md: '50%' } }}>
            <InputLabel id="docType-label">{t('verification.type.document.documentTypeLabel')}</InputLabel>
            <Select
              value={docType}
              labelId="docType-label"
              label={t('verification.type.document.documentTypeLabel')}
              onChange={event => {
                const v = event.target.value as DocType;
                setDocType(v);
                setDocumentRequiredCount(v === 'Offer Letter' ? 1 : v === 'Bank Statement' ? 2 : 0);
              }}
            >
              <MenuItem value="Paystub">{t('verification.type.document.documentTypePaystub')}</MenuItem>
              <MenuItem value="Offer Letter">{t('verification.type.document.documentTypeOfferLetter')}</MenuItem>
              <MenuItem value="Bank Statement">{t('verification.type.document.documentTypeBankStatement')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {docType === 'Paystub' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 1,
            mb: 2,
          }}
        >
          <Typography variant="h6">{t('verification.type.document.selectPayFrequency')}</Typography>
          <FormControl fullWidth sx={{ flexBasis: { md: '50%' } }}>
            <InputLabel id="freq-label">{t('verification.type.document.frequencyLabel')}</InputLabel>
            <Select
              value={frequency}
              labelId="freq-label"
              label={t('verification.type.document.frequencyLabel')}
              placeholder={t('verification.type.document.frequencySelectPlaceholder') as string}
              onChange={event => {
                const v = event.target.value;
                setFrequency(v);
                setDocumentRequiredCount(v === 'weekly' ? 8 : v === 'bi_weekly' ? 4 : v === 'semi_monthly' ? 4 : 2);
              }}
            >
              <MenuItem value="weekly">{t('verification.type.document.frequencyWeekly')}</MenuItem>
              <MenuItem value="bi_weekly">{t('verification.type.document.frequencyBiWeekly')}</MenuItem>
              <MenuItem value="semi_monthly">{t('verification.type.document.frequencySemiMonthly')}</MenuItem>
              <MenuItem value="monthly">{t('verification.type.document.frequencyMonthly')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {(docType === 'Offer Letter' || docType === 'Paystub') && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            type="email"
            label="Employer / HR contact email (optional)"
            placeholder="hr@company.com"
            value={employerEmail}
            onChange={event => setEmployerEmail(event.target.value)}
            helperText="If we can't verify this document, your property may email your employer to confirm your income."
          />
        </Box>
      )}

      {documentRequiredCount > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2, mt: 3, gap: 2 }}>
          {docType === 'Offer Letter' && (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {t('verification.type.document.instructionsOfferLetter')}
            </Typography>
          )}
          {docType === 'Paystub' && (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {t('verification.type.document.instructionsPaystub', {
                count: documentRequiredCount,
                frequency: t(
                  frequency === 'weekly'
                    ? 'verification.type.document.frequencyAdjWeekly'
                    : frequency === 'bi_weekly'
                      ? 'verification.type.document.frequencyAdjBiWeekly'
                      : frequency === 'semi_monthly'
                        ? 'verification.type.document.frequencyAdjSemiMonthly'
                        : 'verification.type.document.frequencyAdjMonthly'
                ),
              })}
            </Typography>
          )}
          {docType === 'Bank Statement' && (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {t('verification.type.document.instructionsBankStatement')}
            </Typography>
          )}
        </Box>
      )}

      {isValidating && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: palette.primary[30] }}>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          <Typography sx={{ fontSize: '14px' }}>{t('verification.type.document.validatingDocument')}</Typography>
        </Box>
      )}

      {validationWarnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: '14px', mb: 0.5 }}>{t('verification.type.document.detectedIssues')}</Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {validationWarnings.map((w, i) => (
              <li key={`warn-${i}`}>
                <Typography sx={{ fontSize: '14px' }}>
                  {w.fileName}: {w.message}
                </Typography>
              </li>
            ))}
          </Box>
          {fallbackTriggered && (
            <Typography sx={{ fontSize: '13px', mt: 1, fontStyle: 'italic' }}>{t('verification.type.document.fallback.stepThreeWarningHint')}</Typography>
          )}
        </Alert>
      )}

      {validationError && showValidationError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => {
            setValidationError('');
            setShowValidationError(false);
          }}
        >
          <Typography sx={{ fontWeight: 'bold', fontSize: '14px' }}>{validationError}</Typography>
        </Alert>
      )}

      {documentRequiredCount > 0 && (
        <Button
          component="label"
          variant="contained"
          fullWidth
          disabled={isValidating}
          sx={{
            textTransform: 'none',
            padding: '0.875rem 1.25rem',
            fontSize: '1rem',
            lineHeight: 1.5,
            fontWeight: 600,
            borderRadius: '8px',
            boxShadow: 'none',
            '&.MuiButton-contained': {
              backgroundColor: palette.primary[30],
              '&:hover': { backgroundColor: palette.primary[50] },
            },
            '&.Mui-disabled': { backgroundColor: palette.grey[30] },
          }}
        >
          {isValidating ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              {t('verification.type.document.validatingDocumentButton')}
            </>
          ) : (
            t('page.continue')
          )}
          <VisuallyHiddenInput ref={fileInputRef} type="file" accept="application/pdf" onChange={e => addFiles(e.target.files)} multiple />
        </Button>
      )}
    </Container>
  );
}
